import { compact, isArray, isEqual, keys } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { FilterClauseType, type FilterableProperty, type FilterClause, type UseFilterBuilderProps } from '../Filters';
import type { BlotterTableFiltersProps } from './BlotterTableFilters.types';
import type { ClientSideFilterRule } from './filters/createClientSideFilter';
import { useClientSideFilter } from './filters/useClientSideFilter';
import {
  cleanupInitialFilterDateRange,
  DATE_RANGE_FILTER_KEYS,
  useDateRangeFilter,
  type DateRangeFilter,
} from './filters/useDateRangeFilter';
import { removeEmptyFilters } from './useWsBlotterTable';

export interface GenericFilter extends DateRangeFilter {
  [key: string]: string | string[] | undefined;
}

export type GenericFilterFilterProps<TFilter extends Record<string, any> = GenericFilter> = {
  initialFilter?: TFilter;
  onFilterChanged?: (filter: TFilter) => void;
  clientSideFilterRules?: ClientSideFilterRule<TFilter>[];
  additionalFilterDateRange?: BlotterTableFiltersProps['dateRange'];
};

// The internal type is Record<string, any> -- the loosest possible. The implementer will apply a type to us.
export const useGenericFilter = function useGenericFilter<TFilter extends Record<string, any> = GenericFilter>(
  filterableProperties: FilterableProperty<any>[],
  filterProps?: GenericFilterFilterProps<TFilter>
) {
  const [filter, setFilter] = useState<TFilter>(() =>
    cleanupInitialFilterDateRange(filterProps?.initialFilter ?? ({} as TFilter))
  );
  const clientSideFilter = useClientSideFilter({
    filter,
    rules: filterProps?.clientSideFilterRules,
  });

  const changeFilter = useCallback(
    (action: SetStateAction<TFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        if (filterProps?.onFilterChanged) {
          filterProps.onFilterChanged(newFilter);
        }
        setFilter(newFilter);
      }
    },
    [filter, filterProps]
  );

  const dateRangeFilter = useDateRangeFilter(filter, changeFilter, filterProps?.additionalFilterDateRange);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters({
          ...curr,
          ...Object.fromEntries(
            [...propertiesByKey.keys()].map(key => {
              const selections = filterClausesByPropertyKey.get(key)?.selections;
              if (selections) {
                return [key, selections];
              } else {
                return [key, []];
              }
            })
          ),
        });

        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      keys(filter).forEach(key => {
        if (DATE_RANGE_FILTER_KEYS.has(key)) {
          return;
        }
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: (isArray(filter[key]) ? filter[key] : compact([filter[key]])) as string[],
        });
      });
    }

    return clauses;
  }, [filter]);

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;

  return {
    filter,
    changeFilter,
    filterBuilderProps,
    blotterTableFilterProps,
    ...clientSideFilter,
  };
};
